import type { AriaAttributes } from 'react';
import { useCallback, cloneElement, forwardRef, isValidElement } from 'react';
import ALink from 'tradera/components/alink/alink';
import LoadingIcon from '@tradera/blueprint/theme/icons2/loading.svg?jsx';

import css from './button.module.scss';
import classNames from 'classnames';
import type { OnlyAs, PolyRefFunction } from 'react-polymorphed';

export type ButtonTheme =
    | 'primary'
    | 'highlight'
    | 'action'
    | 'search'
    | 'warning'
    | 'danger'
    | 'light'
    | 'link'
    | 'translucent'
    | 'unbutton'
    | 'text'
    | 'tag'
    | 'mainMenu'
    | 'dropdown';

export type ButtonVariant = 'filled' | 'outline' | 'ghost' | 'inverted';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export type ButtonProps = AriaAttributes &
    React.PropsWithChildren<{
        theme?: ButtonTheme;
        variant?: ButtonVariant;
        /**
         * Default is 'sm'
         */
        size?: ButtonSize;
        disabled?: boolean;
        className?: string;
        wide?: boolean;
        round?: boolean;
        brandedText?: boolean;
        fluid?: boolean;
        onClick?: (event: React.MouseEvent<HTMLElement>) => void;
        onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
        /**
         * NOTE: If not passing any `className` to the icon it will be set automatically depending on button size and color.
         */
        icon?: React.ReactNode;
        /**
         * Place an icon on the left side of the button. Same as using `icon`.
         */
        iconLeft?: React.ReactNode;
        /**
         * Place an icon on the right side of the button.
         */
        iconRight?: React.ReactNode;
        /**
         * @deprecated Use `iconLeft` or `iconRight` instead to place icons, and `fixedIcon` to fix the icon in place.
         * `position` defaults to `left`
         *
         * { position: 'left' | 'right', variant: 'fixed'}
         */
        iconConfig?: {
            position?: 'left' | 'right';
            variant?: 'fixed';
        };
        /**
         * Place an icon to the outer left or right of the button
         */
        fixedIcon?: boolean;
        href?: string;
        target?: '_blank' | '_self' | '_parent' | '_top';

        loading?: boolean;
        input?: boolean;
        nowrap?: boolean;
        /**
         * Default is 'center'
         */
        align?: 'left' | 'center' | 'right';
        type?: 'submit' | 'button';
        title?: string;
        alt?: string;
    }>;

const polyRef = forwardRef as PolyRefFunction;

const getClassNameFromReactElement = (element: React.ReactNode) => {
    const props = isValidElement(element) ? element?.props : undefined;
    return typeof props === 'object' &&
        props &&
        'className' in props &&
        props.className
        ? (props?.className as string)
        : '';
};

const ButtonIcon = ({
    icon: IconComponent,
    size,
    round,
    className
}: {
    icon?: React.ReactNode;
    size: ButtonProps['size'];
    round: ButtonProps['round'];
    className?: string;
}) => {
    if (!IconComponent) return null;
    if (!isValidElement(IconComponent)) return null;

    const iconClasses = getClassNameFromReactElement(IconComponent);

    return (
        <>
            {cloneElement(IconComponent, {
                ...IconComponent?.props,
                // Set default classes if none are provided
                className: iconClasses
                    ? iconClasses
                    : classNames(
                          'icon',
                          {
                              'icon-xs': size === 'xs',
                              'icon-sm': size === 'sm',
                              'icon-md': size === 'md' || size === 'lg',
                              'icon-lg': round && size === 'lg'
                          },
                          className
                      )
            })}
        </>
    );
};

export const Button = polyRef<
    'button',
    ButtonProps,
    OnlyAs<'button' | 'input' | 'a'>
>(
    (
        {
            theme = 'primary',
            variant = 'filled',
            size = 'sm',
            children,
            disabled,
            wide,
            round,
            className,
            brandedText,
            fluid,
            onClick,
            icon: iconProp,
            iconLeft: iconLeftProp,
            iconRight: iconRightProp,
            iconConfig: iconConfigProp,
            href,
            loading,
            input,
            align = 'center',
            nowrap,
            fixedIcon,
            ...otherProps
        },
        forwardedRef
    ) => {
        const iconConfig = { position: 'left', ...iconConfigProp };

        const isIconOnRight = iconConfig?.position === 'right';
        const isIconOnLeft = iconConfig?.position === 'left';
        const iconLeft = iconLeftProp ?? (isIconOnLeft ? iconProp : null);
        const iconRight = iconRightProp ?? (isIconOnRight ? iconProp : null);
        const isFixedIcon = fixedIcon ?? iconConfig?.variant === 'fixed';

        const ariaDisabled = otherProps['aria-disabled'];

        const buttonClasses = classNames(
            css.button,
            css.theme,
            css[`theme-${theme}`],
            {
                [css.filled]: variant === 'filled',
                [css.outline]: variant === 'outline',
                [css.ghost]: variant === 'ghost',
                [css.inverted]: variant === 'inverted',
                [css.brandedText]: brandedText,
                [css[`size-${size}`]]: !!size && size !== 'sm',
                [css.wide]: wide,
                [css.round]: round,
                [css.fluid]: fluid,
                [css.loading]: loading,
                'text-nowrap text-truncate': nowrap,
                [css.fixed]: iconConfig.variant === 'fixed',
                ['text-left']: align === 'left',
                ['text-right']: align === 'right',
                // otherwise icons won't be out to the edge on larger sizes when using fixed icons since we pad them a lot.
                'px-2': fluid && isFixedIcon
            },
            className
        );

        const onBtnClick = useCallback(
            (
                event: React.MouseEvent<
                    HTMLAnchorElement | HTMLButtonElement,
                    MouseEvent
                >
            ) => {
                if (ariaDisabled === true || ariaDisabled === 'true') return;

                if (typeof onClick === 'function' && !loading) {
                    onClick(event);
                }
            },
            [onClick, loading, ariaDisabled]
        );

        if (input) {
            return <input className={buttonClasses} {...otherProps} />;
        }

        const loadingContent = loading ? (
            <span className={css.loadingIcon}>
                <LoadingIcon
                    className={classNames('icon icon-spin', {
                        'icon-xs': size === 'xs',
                        'icon-md': size === 'md',
                        'icon-lg': size === 'lg'
                    })}
                />
                <span
                    className="visually-hidden"
                    role="alert"
                    aria-live="assertive">
                    Loading
                </span>
            </span>
        ) : null;

        /**
         * Queue some complexity for handling all different positionings of icons.
         *
         * Since we want to be able to both have icons on the left and right side of
         * the inner content, AND be able to align the text to the left, center or
         * right, AND be able to have icons fixed, we need to do some codez to make
         * things predictable. Sure, we could absolutely position the icons, but
         * that could bring some unexpected behaviour, so let's use flexbox instead.
         *
         * We solve this by having a wrapper around the icon(s) and the content. And
         * then we place elements on both sides of the content, no matter where
         * the icon(s) are. We then flex-grow each element to be able to center the
         * content if we want to.
         */
        const fixedToLeft = isFixedIcon && align !== 'left';
        const fixedToRight = isFixedIcon && align !== 'right';
        const content =
            !!iconLeft || !!iconRight ? (
                <span
                    className={classNames('d-flex align-items-center', {
                        'justify-content-center': align === 'center',
                        'justify-content-end': align === 'right'
                    })}>
                    <span
                        // Unfortunately bootstrap's flex-fill sets flex: 1 1 auto; but we want flex: 1 1 0%;
                        style={{
                            ...(isFixedIcon && align !== 'left'
                                ? { flex: 1 }
                                : {})
                        }}
                        className={classNames('d-flex', {
                            'visibility-hidden': !iconLeft,
                            'mr-1': !!children && !!iconLeft && !fixedToLeft
                        })}>
                        <ButtonIcon icon={iconLeft} size={size} round={round} />
                    </span>
                    {children}
                    {loadingContent}
                    <span
                        // Unfortunately bootstrap's flex-fill sets flex: 1 1 auto; but we want flex: 1 1 0%;
                        style={{
                            ...(isFixedIcon && align !== 'right'
                                ? { flex: 1 }
                                : {})
                        }}
                        className={classNames('d-flex justify-content-end', {
                            'visibility-hidden': !iconRight,
                            'ml-1': !!children && !!iconRight && !fixedToRight
                        })}>
                        <ButtonIcon
                            icon={iconRight}
                            size={size}
                            round={round}
                        />
                    </span>
                </span>
            ) : (
                <>
                    {children}
                    {loadingContent}
                </>
            );

        // An <a> element cannot be disabled, therefore we cannot easily get a disabled behavior for it without modifying a lot of props,
        // so easiest way to solve this is perhaps to just render a disabled <button>.
        return href && !disabled ? (
            <ALink
                href={href}
                className={buttonClasses}
                onClick={onBtnClick}
                ref={forwardedRef}
                {...otherProps}>
                {content}
            </ALink>
        ) : (
            <button
                className={buttonClasses}
                onClick={onBtnClick}
                disabled={disabled}
                {...otherProps}
                ref={forwardedRef}>
                {content}
            </button>
        );
    }
);

Button.displayName = 'Button';
